/**
 * Shared statutory rates shape — mirrors StatutoryRatesManager.jsx.
 * Simulator + structures must use this (not flat ESI_RATE / PF_RATE stubs).
 * PT jurisdiction for payroll/simulator = employee branch.state.
 */

import {
  normalizePtStatesMap,
  normalizePtStateConfig,
  calculatePT,
  getPtFromSlabs as getPtFromSlabsEngine,
} from "./professionalTaxEngine";

function buildDefaultPtStates() {
  return {
    Karnataka: {
      applicable: true,
      frequency: "MONTHLY",
      grossBasis: "MONTHLY_GROSS",
      /** ₹200 × 11 months + ₹300 in February */
      annualCap: 2500,
      needsVerification: false,
      lastVerified: "2026-04-01",
      sourceNotes:
        "FY 2026-27 — ₹200/month above ₹25,000; February ₹300 (same taxable band)",
      slabs: [
        { min: 0, max: 24999, rate: 0 },
        { min: 25000, max: Infinity, rate: 200 },
      ],
      genderSlabs: null,
      specialRules: [
        {
          type: "MONTH_OVERRIDE",
          month: 2,
          minGross: 25000,
          replaceAmount: 300,
        },
      ],
      deductMonths: null,
      halfYearlyMode: "ON_SCHEDULE",
    },
    Maharashtra: {
      applicable: true,
      frequency: "MONTHLY",
      grossBasis: "MONTHLY_GROSS",
      annualCap: 2500,
      needsVerification: true,
      lastVerified: null,
      sourceNotes:
        "FY 2026-27 seed — female threshold & Feb override editable; verify on mahagst.gov.in",
      slabs: [
        { min: 0, max: 7500, rate: 0 },
        { min: 7501, max: 10000, rate: 175 },
        { min: 10001, max: Infinity, rate: 200 },
      ],
      genderSlabs: {
        male: [
          { min: 0, max: 7500, rate: 0 },
          { min: 7501, max: 10000, rate: 175 },
          { min: 10001, max: Infinity, rate: 200 },
        ],
        female: [
          { min: 0, max: 25000, rate: 0 },
          { min: 25001, max: Infinity, rate: 200 },
        ],
        other: [
          { min: 0, max: 7500, rate: 0 },
          { min: 7501, max: 10000, rate: 175 },
          { min: 10001, max: Infinity, rate: 200 },
        ],
      },
      specialRules: [
        { type: "MONTH_OVERRIDE", month: 2, gender: "male", minGross: 10001, replaceAmount: 300 },
        { type: "MONTH_OVERRIDE", month: 2, gender: "female", minGross: 25001, replaceAmount: 300 },
      ],
      deductMonths: null,
      halfYearlyMode: "ON_SCHEDULE",
    },
    "Tamil Nadu": {
      applicable: true,
      frequency: "HALF_YEARLY",
      grossBasis: "MONTHLY_GROSS",
      annualCap: 2500,
      needsVerification: true,
      lastVerified: null,
      sourceNotes: "Half-yearly (Sep & Mar). Rate is half-year amount when ON_SCHEDULE.",
      slabs: [
        { min: 0, max: 21000, rate: 0 },
        { min: 21001, max: Infinity, rate: 1250 },
      ],
      genderSlabs: null,
      specialRules: [],
      deductMonths: [9, 3],
      halfYearlyMode: "ON_SCHEDULE",
    },
    Kerala: {
      applicable: true,
      frequency: "HALF_YEARLY",
      grossBasis: "MONTHLY_GROSS",
      annualCap: 2500,
      needsVerification: true,
      lastVerified: null,
      sourceNotes: "Half-yearly (Aug & Feb typical). Verify deduct months with local PTRC.",
      slabs: [
        { min: 0, max: 1999, rate: 0 },
        { min: 2000, max: 2999, rate: 20 },
        { min: 3000, max: 4999, rate: 30 },
        { min: 5000, max: 7499, rate: 50 },
        { min: 7500, max: 9999, rate: 75 },
        { min: 10000, max: 12499, rate: 100 },
        { min: 12500, max: Infinity, rate: 125 },
      ],
      genderSlabs: null,
      specialRules: [],
      deductMonths: [8, 2],
      halfYearlyMode: "ON_SCHEDULE",
    },
    "West Bengal": {
      applicable: true,
      frequency: "MONTHLY",
      grossBasis: "MONTHLY_GROSS",
      annualCap: 2400,
      needsVerification: true,
      lastVerified: null,
      sourceNotes: "FY 2026-27 seed — verify with WB PT schedule",
      slabs: [
        { min: 0, max: 10000, rate: 110 },
        { min: 10001, max: 15000, rate: 130 },
        { min: 15001, max: 25000, rate: 150 },
        { min: 25001, max: Infinity, rate: 200 },
      ],
      genderSlabs: null,
      specialRules: [],
      deductMonths: null,
      halfYearlyMode: "ON_SCHEDULE",
    },
    Gujarat: {
      applicable: true,
      frequency: "MONTHLY",
      grossBasis: "MONTHLY_GROSS",
      annualCap: 2400,
      needsVerification: true,
      lastVerified: null,
      sourceNotes: "FY 2026-27 seed",
      slabs: [
        { min: 0, max: 5999, rate: 0 },
        { min: 6000, max: 8999, rate: 80 },
        { min: 9000, max: 11999, rate: 150 },
        { min: 12000, max: Infinity, rate: 200 },
      ],
      genderSlabs: null,
      specialRules: [],
      deductMonths: null,
      halfYearlyMode: "ON_SCHEDULE",
    },
    Delhi: {
      applicable: false,
      frequency: "MONTHLY",
      grossBasis: "MONTHLY_GROSS",
      annualCap: null,
      needsVerification: false,
      lastVerified: "2026-04-01",
      sourceNotes: "Professional Tax not levied in Delhi",
      slabs: [{ min: 0, max: Infinity, rate: 0 }],
      genderSlabs: null,
      specialRules: [],
      deductMonths: null,
      halfYearlyMode: "ON_SCHEDULE",
    },
  };
}

export const DEFAULT_STATUTORY_RATES = {
  pf: {
    employeeShare: 12,
    employerShare: 12,
    pfWageCeiling: 15000,
    epsWageCeiling: 15000,
    edliWageCeiling: 15000,
    ceiling: 15000, // legacy alias for pfWageCeiling
    epf: 3.67, // employer EPF (to employee PF account)
    eps: 8.33, // employer EPS (pension)
    adminCharges: 0.5,
    edliRate: 0.5,
    /** EPFO establishment-level minimum admin charges per month (₹). */
    adminChargesMinimum: 500,
    prorateCeiling: false,
  },
  esi: {
    employeeShare: 0.75,
    employerShare: 3.25,
    ceiling: 21000,
    cutoff: 21000,
  },
  pt: {
    /** Admin UI editing focus only — payroll uses branch.state */
    currentState: "Karnataka",
    states: buildDefaultPtStates(),
  },
  gratuity: {
    rate: 4.81,
    minServiceYearsFullTime: 5,
    minServiceYearsContract: 5,
  },
};

/** Minimum service years for gratuity eligibility by employment type (default 5). */
export function resolveGratuityMinServiceYears(gratuityRates, employmentType) {
  const key = String(employmentType || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  const raw =
    key === "full_time" || key === "fulltime"
      ? gratuityRates?.minServiceYearsFullTime
      : key === "contract"
      ? gratuityRates?.minServiceYearsContract
      : null;
  const n = Number(raw);
  return raw != null && raw !== "" && Number.isFinite(n) && n >= 0 ? n : 5;
}

export const STATUTORY_RATES_STORAGE_KEY = "pacehrm.salaryStatutoryRates";
export const STATUTORY_RATES_CHANGED_EVENT = "pacehrm:statutory-rates-changed";

function revivePtSlabs(states = {}) {
  const mapped = normalizePtStatesMap(states);
  const out = {};
  Object.keys(mapped).forEach((state) => {
    const cfg = mapped[state];
    const reviveList = (list) =>
      (list || []).map((slab) => ({
        ...slab,
        min: Number(slab.min) || 0,
        max:
          slab.max == null ||
          slab.max === "" ||
          slab.max === Infinity ||
          String(slab.max).toLowerCase() === "infinity"
            ? Infinity
            : Number(slab.max),
        rate: Number(slab.rate) || 0,
      }));
    out[state] = {
      ...cfg,
      slabs: reviveList(cfg.slabs),
      genderSlabs: cfg.genderSlabs
        ? {
            male: reviveList(cfg.genderSlabs.male),
            female: reviveList(cfg.genderSlabs.female),
            other: reviveList(cfg.genderSlabs.other),
          }
        : null,
    };
  });
  return out;
}

function maybeUpgradeLegacyKarnataka(states) {
  let next = states;
  const ka = states?.Karnataka;
  if (ka) {
    const slabs = Array.isArray(ka) ? ka : ka.slabs;
    const first = Array.isArray(slabs) && slabs[0];
    const isLegacy =
      first &&
      Number(first.min) === 0 &&
      Number(first.max) === 15000 &&
      Number(first.rate) === 200;
    if (isLegacy) {
      next = { ...next, Karnataka: buildDefaultPtStates().Karnataka };
    } else {
      // Ensure February ₹300 override exists for current KA ₹200 taxable pattern
      const cfg = typeof ka === "object" && !Array.isArray(ka) ? ka : { slabs: ka };
      const rules = Array.isArray(cfg.specialRules) ? cfg.specialRules : [];
      const hasFeb = rules.some(
        (r) =>
          String(r?.type || "").toUpperCase() === "MONTH_OVERRIDE" &&
          Number(r.month) === 2
      );
      if (!hasFeb) {
        next = {
          ...next,
          Karnataka: {
            ...cfg,
            annualCap: cfg.annualCap == null || Number(cfg.annualCap) === 2400 ? 2500 : cfg.annualCap,
            specialRules: [
              ...rules,
              {
                type: "MONTH_OVERRIDE",
                month: 2,
                minGross: 25000,
                replaceAmount: 300,
              },
            ],
            sourceNotes:
              cfg.sourceNotes ||
              "FY 2026-27 — ₹200/month above ₹25,000; February ₹300",
          },
        };
      }
    }
  }
  return next;
}

function normalizePfFields(pf = {}) {
  const pfWageCeiling =
    Number(pf.pfWageCeiling ?? pf.ceiling ?? 15000) || 15000;
  const epsWageCeiling = Number(pf.epsWageCeiling ?? 15000) || 15000;
  const edliWageCeiling = Number(pf.edliWageCeiling ?? 15000) || 15000;
  let epf = Number(pf.epf);
  let eps = Number(pf.eps);
  if (!Number.isFinite(epf) || !Number.isFinite(eps)) {
    epf = 3.67;
    eps = 8.33;
  } else if (epf > 5 && eps < 5) {
    // legacy UI stored EPF/EPS percentages swapped
    const tmp = epf;
    epf = eps;
    eps = tmp;
  }
  return {
    ...pf,
    employeeShare: Number(pf.employeeShare ?? 12) || 12,
    employerShare: Number(pf.employerShare ?? 12) || 12,
    pfWageCeiling,
    epsWageCeiling,
    edliWageCeiling,
    ceiling: pfWageCeiling,
    epf,
    eps,
    adminCharges: Number(pf.adminCharges ?? 0.5) || 0,
    edliRate: Number(pf.edliRate ?? 0.5) || 0,
    adminChargesMinimum: Number(pf.adminChargesMinimum ?? 500) || 500,
  };
}

/** Pick rate snapshot valid on effectiveDate from section history (latest entry on or before date). */
export function resolveRatesSectionForDate(sectionHistory, fallbackRates, effectiveDate) {
  if (!effectiveDate || !Array.isArray(sectionHistory) || sectionHistory.length === 0) {
    return fallbackRates;
  }
  const target = String(effectiveDate).slice(0, 10);
  const sorted = [...sectionHistory].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );
  const match = sorted.find(
    (entry) => entry.active !== false && String(entry.date).slice(0, 10) <= target
  );
  if (!match?.rates) return fallbackRates;
  return { ...fallbackRates, ...match.rates };
}

/** Resolve PT state config (or slabs) for a state on effectiveDate. */
export function resolvePtSlabsForDate(ptHistoryForState, fallbackSlabsOrConfig, effectiveDate) {
  const fallbackCfg = normalizePtStateConfig(fallbackSlabsOrConfig);
  if (!effectiveDate || !Array.isArray(ptHistoryForState) || ptHistoryForState.length === 0) {
    return fallbackCfg.slabs;
  }
  const target = String(effectiveDate).slice(0, 10);
  const sorted = [...ptHistoryForState].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );
  const match = sorted.find(
    (entry) => entry.active !== false && String(entry.date).slice(0, 10) <= target
  );
  if (!match?.rates) return fallbackCfg.slabs;
  if (match.rates.slabs || match.rates.frequency) {
    return normalizePtStateConfig(match.rates).slabs;
  }
  return match.rates?.slabs || fallbackCfg.slabs;
}

export function resolvePtStateConfigForDate(ptHistoryForState, fallbackConfig, effectiveDate) {
  const fallbackCfg = normalizePtStateConfig(fallbackConfig);
  if (!effectiveDate || !Array.isArray(ptHistoryForState) || ptHistoryForState.length === 0) {
    return fallbackCfg;
  }
  const target = String(effectiveDate).slice(0, 10);
  const sorted = [...ptHistoryForState].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );
  const match = sorted.find(
    (entry) => entry.active !== false && String(entry.date).slice(0, 10) <= target
  );
  if (!match?.rates) return fallbackCfg;
  return normalizePtStateConfig({ ...fallbackCfg, ...match.rates });
}

/**
 * Resolve full rates object from persisted config + effective date (YYYY-MM or YYYY-MM-DD).
 * Pay month YYYY-MM → month-end as-of (mid-month history applies for that whole month).
 */
export function resolveStatutoryRatesForEffectiveDate(configJson, effectiveDate) {
  const base = mergeStatutoryRates(configJson?.rates || configJson);
  const dateKey = effectiveDateFromMonth(effectiveDate);
  const history = configJson?.history || {};

  const pf = normalizePfFields(
    resolveRatesSectionForDate(history.pf, base.pf, dateKey)
  );
  const esi = resolveRatesSectionForDate(history.esi, base.esi, dateKey);
  const gratuity = resolveRatesSectionForDate(history.gratuity, base.gratuity, dateKey);

  const pt = { ...base.pt };
  const ptLiveEffective = String(configJson?.effectiveDates?.pt || "").slice(0, 10);
  if (pt.states && history.pt) {
    const nextStates = { ...pt.states };
    Object.keys(nextStates).forEach((stateName) => {
      const hist = history.pt[stateName];
      if (Array.isArray(hist) && hist.length && ptLiveEffective) {
        const sorted = [...hist].sort((a, b) => new Date(b.date) - new Date(a.date));
        const match = sorted.find(
          (entry) => entry.active !== false && String(entry.date).slice(0, 10) <= dateKey
        );
        const histDate = match?.date ? String(match.date).slice(0, 10) : null;
        // Live Structure PT published on/after history snapshot → keep live slabs
        if (histDate && ptLiveEffective >= histDate) {
          return;
        }
      }
      nextStates[stateName] = resolvePtStateConfigForDate(
        history.pt[stateName],
        nextStates[stateName],
        dateKey
      );
    });
    pt.states = revivePtSlabs(nextStates);
  }

  // Income tax: prefer regime-specific history (StatutoryRatesManager Old/New cards)
  let incomeTax = { ...base.incomeTax };
  const applyRegimeHistory = (sectionKey, regimeKey, defaultStd) => {
    const hist = history[sectionKey];
    if (!Array.isArray(hist) || !hist.length) return;
    const match = resolveRatesSectionForDate(hist, null, dateKey);
    if (!match) return;
    incomeTax = {
      ...incomeTax,
      cess: match.cess != null ? Number(match.cess) : incomeTax.cess,
      slabs: {
        ...incomeTax.slabs,
        [regimeKey]: Array.isArray(match.slabs) ? match.slabs : incomeTax.slabs?.[regimeKey],
      },
      surcharge: {
        ...incomeTax.surcharge,
        [regimeKey]: Array.isArray(match.surcharge)
          ? match.surcharge
          : incomeTax.surcharge?.[regimeKey],
      },
      standardDeduction: {
        ...(typeof incomeTax.standardDeduction === "object"
          ? incomeTax.standardDeduction
          : { old: 50000, new: 75000 }),
        [regimeKey]:
          Number(match.standardDeduction) ||
          Number(
            typeof incomeTax.standardDeduction === "object"
              ? incomeTax.standardDeduction[regimeKey]
              : incomeTax.standardDeduction
          ) ||
          defaultStd,
      },
    };
  };
  applyRegimeHistory("incomeTaxOld", "old", 50000);
  applyRegimeHistory("incomeTaxNew", "new", 75000);
  if (
    (!history.incomeTaxOld?.length || !history.incomeTaxNew?.length) &&
    Array.isArray(history.incomeTax) &&
    history.incomeTax.length
  ) {
    const combined = resolveRatesSectionForDate(history.incomeTax, incomeTax, dateKey);
    if (combined) {
      incomeTax = {
        ...incomeTax,
        ...combined,
        slabs: combined.slabs?.old || combined.slabs?.new
          ? {
              old: combined.slabs?.old || incomeTax.slabs?.old,
              new: combined.slabs?.new || incomeTax.slabs?.new,
            }
          : incomeTax.slabs,
      };
    }
  }

  return mergeStatutoryRates({ pf, esi, gratuity, pt, incomeTax });
}

/**
 * Pay month YYYY-MM → month-end for history lookup so a mid-month
 * statutory revision applies for the entire pay month.
 * Explicit YYYY-MM-DD is unchanged.
 */
export function effectiveDateFromMonth(monthStr) {
  if (!monthStr) return new Date().toISOString().slice(0, 10);
  const s = String(monthStr).trim();
  if (s.length === 7) {
    const m = s.match(/^(\d{4})-(\d{2})$/);
    if (!m) return `${s}-01`;
    const year = Number(m[1]);
    const month = Number(m[2]);
    if (month < 1 || month > 12) return `${s}-01`;
    const calendarDays = new Date(year, month, 0).getDate();
    return `${year}-${String(month).padStart(2, "0")}-${String(calendarDays).padStart(2, "0")}`;
  }
  return s.slice(0, 10);
}

/** Deep-merge saved rates onto defaults (keeps nested pf/esi/pt/gratuity). */
export function mergeStatutoryRates(partial) {
  const base = JSON.parse(JSON.stringify(DEFAULT_STATUTORY_RATES));
  if (!partial || typeof partial !== "object") {
    base.pf = normalizePfFields(base.pf);
    return base;
  }

  if (partial.pf && typeof partial.pf === "object") {
    base.pf = normalizePfFields({ ...base.pf, ...partial.pf });
  } else {
    base.pf = normalizePfFields(base.pf);
  }
  if (partial.esi && typeof partial.esi === "object") {
    base.esi = { ...base.esi, ...partial.esi };
  }
  if (partial.gratuity && typeof partial.gratuity === "object") {
    base.gratuity = { ...base.gratuity, ...partial.gratuity };
  }
  if (partial.pt && typeof partial.pt === "object") {
    base.pt = {
      ...base.pt,
      currentState: partial.pt.currentState || base.pt.currentState,
      states: revivePtSlabs(
        maybeUpgradeLegacyKarnataka({
          ...base.pt.states,
          ...(partial.pt.states || {}),
        })
      ),
    };
  }

  // Legacy flat keys (old EmployeeSalaryStructureManager stub)
  if (partial.PF_RATE != null && !partial.pf) {
    base.pf.employeeShare = Number(partial.PF_RATE) || base.pf.employeeShare;
  }
  if (partial.PF_EMPLOYER_RATE != null && !partial.pf) {
    base.pf.employerShare = Number(partial.PF_EMPLOYER_RATE) || base.pf.employerShare;
  }
  if (partial.MAX_PF_LIMIT != null && !partial.pf) {
    base.pf.ceiling = Number(partial.MAX_PF_LIMIT) || base.pf.ceiling;
  }
  if (partial.ESI_EMPLOYEE_RATE != null && !partial.esi) {
    base.esi.employeeShare = Number(partial.ESI_EMPLOYEE_RATE) || base.esi.employeeShare;
  }
  if (partial.ESI_EMPLOYER_RATE != null && !partial.esi) {
    base.esi.employerShare = Number(partial.ESI_EMPLOYER_RATE) || base.esi.employerShare;
  }
  // Never treat combined ESI_RATE as employer-only — ignore legacy 1.75 stub
  if (partial.ESI_CEILING != null && !partial.esi) {
    base.esi.ceiling = Number(partial.ESI_CEILING) || base.esi.ceiling;
    base.esi.cutoff = base.esi.ceiling;
  }
  if (partial.GRATUITY_RATE != null && !partial.gratuity) {
    base.gratuity.rate = Number(partial.GRATUITY_RATE) || base.gratuity.rate;
  }

  return base;
}

export function loadStatutoryRatesFromStorage() {
  try {
    const raw = localStorage.getItem(STATUTORY_RATES_STORAGE_KEY);
    if (!raw) return mergeStatutoryRates(null);
    return mergeStatutoryRates(JSON.parse(raw));
  } catch {
    return mergeStatutoryRates(null);
  }
}

export function saveStatutoryRatesToStorage(rates) {
  const merged = mergeStatutoryRates(rates);
  // Persist Infinity as null for JSON
  const toStore = JSON.parse(
    JSON.stringify(merged, (_k, v) => (v === Infinity ? null : v))
  );
  localStorage.setItem(STATUTORY_RATES_STORAGE_KEY, JSON.stringify(toStore));
  window.dispatchEvent(
    new CustomEvent(STATUTORY_RATES_CHANGED_EVENT, { detail: merged })
  );
  return merged;
}

/**
 * Normalize any rates object into simulator config:
 * { pf, esi, gratuity, ptSlabs, ptState, ptStateConfig }
 */
export function buildSimulatorStatutoryConfig(ratesInput, ptState) {
  const r = mergeStatutoryRates(ratesInput);
  const state =
    ptState ||
    r.pt?.currentState ||
    "Karnataka";
  const ptStateConfig = normalizePtStateConfig(
    r.pt?.states?.[state] || DEFAULT_STATUTORY_RATES.pt.states.Karnataka,
    state
  );
  const ptSlabs = (ptStateConfig.slabs || []).map((s) => ({
    min: Number(s.min) || 0,
    max:
      s.max == null || s.max === Infinity || String(s.max).toLowerCase() === "infinity"
        ? Infinity
        : Number(s.max),
    rate: Number(s.rate) || 0,
  }));

  return {
    pf: {
      employeeShare: Number(r.pf.employeeShare ?? 12) || 12,
      employerShare: Number(r.pf.employerShare ?? 12) || 12,
      // Match StatutoryRatesManager defaults (0.5%) — do not collapse missing to 0
      adminCharges: Number(r.pf.adminCharges ?? 0.5),
      edliRate: Number(r.pf.edliRate ?? 0.5),
      adminChargesMinimum: Number(r.pf.adminChargesMinimum ?? 500) || 500,
      pfWageCeiling: Number(r.pf.pfWageCeiling ?? r.pf.ceiling) || 15000,
      epsWageCeiling: Number(r.pf.epsWageCeiling) || 15000,
      edliWageCeiling: Number(r.pf.edliWageCeiling) || 15000,
      ceiling: Number(r.pf.pfWageCeiling ?? r.pf.ceiling) || 15000,
      epf: Number(r.pf.epf ?? 3.67) || 3.67,
      eps: Number(r.pf.eps ?? 8.33) || 8.33,
      prorateCeiling:
        r.pf.prorateCeiling === true ||
        r.pf.prorateCeiling === 1 ||
        r.pf.prorateCeiling === "true" ||
        r.pf.prorateCeiling === "yes",
    },
    esi: {
      employeeShare: Number(r.esi.employeeShare) || 0.75,
      employerShare: Number(r.esi.employerShare) || 3.25,
      ceiling: Number(r.esi.ceiling) || 21000,
      cutoff: Number(r.esi.cutoff ?? r.esi.ceiling) || 21000,
    },
    gratuity: {
      rate: Number(r.gratuity.rate) || 4.81,
    },
    ptSlabs,
    ptState: state,
    ptStateConfig,
  };
}

/** PT amount from state slabs (gross = monthly gross for PT). */
export function getPtFromSlabs(gross, slabs) {
  return getPtFromSlabsEngine(gross, slabs);
}

/**
 * Full PT calc using branch-state config (gender / half-year / Feb / cap).
 */
export function computePtAmount({
  monthlyGross,
  ptStateConfig,
  ptSlabs,
  gender,
  month,
  ptApplicable = true,
  ytdPtAmount = 0,
  stateName = "",
}) {
  const result = calculatePT({
    stateConfig: ptStateConfig || ptSlabs,
    monthlyGross,
    gender,
    month,
    ptApplicable,
    ytdPtAmount,
    stateName,
  });
  return result.amount;
}

/**
 * ESI contributions.
 * Eligibility: opts.eligibilityGross (or gross) ≤ cutoff, and applicable === true.
 * Contribution wage: min(gross, cutoff) — Cutoff is the contribution ceiling
 * (e.g. earn ₹28,000 with Cutoff ₹21,000 → contribute on ₹21,000;
 *  Cutoff above earn → contribute on full earn).
 * Employee + employer always rounded UP.
 */
export function computeEsiAmounts(gross, esiConfig, applicable, opts = {}) {
  if (!applicable) return { employee: 0, employer: 0, eligible: false };
  const cutoff = Number(esiConfig.cutoff ?? esiConfig.ceiling) || 21000;
  const contributionRaw = Number(gross) || 0;
  const eligibility = Number(
    opts.eligibilityGross != null ? opts.eligibilityGross : contributionRaw
  );
  if (!(eligibility > 0 && eligibility <= cutoff)) {
    return { employee: 0, employer: 0, eligible: false };
  }
  if (contributionRaw <= 0) {
    return { employee: 0, employer: 0, eligible: true };
  }
  const contribution = Math.min(contributionRaw, cutoff);
  const empPct = Number(esiConfig.employeeShare) || 0.75;
  const emprPct = Number(esiConfig.employerShare) || 3.25;
  return {
    employee: Math.ceil((contribution * empPct) / 100),
    employer: Math.ceil((contribution * emprPct) / 100),
    eligible: true,
  };
}

/** Full income tax block for StatutoryRatesManager initial state */
export function getDefaultIncomeTaxRates() {
  return {
    cess: 4,
    surcharge: {
      old: [
        { min: 0, max: 5000000, rate: 0 },
        { min: 5000001, max: 10000000, rate: 10 },
        { min: 10000001, max: 20000000, rate: 15 },
        { min: 20000001, max: 50000000, rate: 25 },
        { min: 50000001, max: Infinity, rate: 37 },
      ],
      new: [
        { min: 0, max: 5000000, rate: 0 },
        { min: 5000001, max: 10000000, rate: 10 },
        { min: 10000001, max: 20000000, rate: 15 },
        { min: 20000001, max: 50000000, rate: 25 },
        { min: 50000001, max: Infinity, rate: 25 },
      ],
    },
    slabs: {
      old: [
        { min: 0, max: 250000, rate: 0 },
        { min: 250001, max: 500000, rate: 5 },
        { min: 500001, max: 1000000, rate: 20 },
        { min: 1000001, max: Infinity, rate: 30 },
      ],
      new: [
        { min: 0, max: 400000, rate: 0 },
        { min: 400001, max: 800000, rate: 5 },
        { min: 800001, max: 1200000, rate: 10 },
        { min: 1200001, max: 1600000, rate: 15 },
        { min: 1600001, max: 2000000, rate: 20 },
        { min: 2000001, max: 2400000, rate: 25 },
        { min: 2400001, max: Infinity, rate: 30 },
      ],
    },
  };
}

export const STATUTORY_BASELINE_DATE = "2026-04-01";

/**
 * Build payload for API. Live `rates` are derived from history ≤ today so
 * future-dated revisions do not affect payroll until their effective date.
 */
export function buildStatutoryConfigPayload(rates, history, effectiveDates) {
  const today = new Date().toISOString().slice(0, 10);
  const draft = {
    schemaVersion: 2,
    rates,
    history,
    effectiveDates,
  };
  // Derive live PF/ESI/PT/gratuity/IT from history as of today
  const liveSlice = resolveStatutoryRatesForEffectiveDate(draft, today);
  const liveRates = {
    ...mergeStatutoryRates(rates),
    pf: liveSlice.pf,
    esi: liveSlice.esi,
    gratuity: liveSlice.gratuity,
    pt: {
      ...mergeStatutoryRates(rates).pt,
      ...liveSlice.pt,
      currentState: rates?.pt?.currentState || "Karnataka",
    },
    incomeTax: liveSlice.incomeTax || rates.incomeTax,
  };

  return JSON.parse(
    JSON.stringify(
      {
        schemaVersion: 2,
        rates: liveRates,
        history,
        effectiveDates,
        // Editor snapshot (may include future-dated edits still open in the form)
        editorRates: rates,
      },
      (_k, v) => (v === Infinity ? null : v)
    )
  );
}

/** Apply API config_json onto StatutoryRatesManager state */
export function applyStatutoryConfigJson(configJson, fallbackRates) {
  const baseRates = mergeStatutoryRates(fallbackRates);
  if (!configJson || typeof configJson !== "object") {
    return { rates: { ...baseRates, incomeTax: getDefaultIncomeTaxRates() } };
  }

  const rates = configJson.rates
    ? {
        ...mergeStatutoryRates(configJson.rates),
        incomeTax: configJson.rates.incomeTax || getDefaultIncomeTaxRates(),
      }
    : { ...baseRates, incomeTax: getDefaultIncomeTaxRates() };

  const reviveHistoryPt = (ptHistory = {}) => {
    const out = {};
    Object.keys(ptHistory).forEach((state) => {
      out[state] = (ptHistory[state] || []).map((entry) => {
        const rates = entry.rates || {};
        if (rates.frequency || rates.annualCap != null || rates.genderSlabs || Array.isArray(rates.slabs)) {
          return {
            ...entry,
            rates: normalizePtStateConfig(rates, state),
          };
        }
        return {
          ...entry,
          rates: {
            ...rates,
            slabs: (rates.slabs || []).map((slab) => ({
              ...slab,
              min: Number(slab.min) || 0,
              max:
                slab.max == null ||
                slab.max === Infinity ||
                String(slab.max).toLowerCase() === "infinity"
                  ? Infinity
                  : Number(slab.max),
              rate: Number(slab.rate) || 0,
            })),
          },
        };
      });
    });
    return out;
  };

  return {
    rates,
    history: configJson.history
      ? {
          ...configJson.history,
          pt: reviveHistoryPt(configJson.history.pt),
        }
      : undefined,
    effectiveDates: configJson.effectiveDates,
  };
}
